/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled, { css } from "styled-components";
import { devices } from "components/shared/Styles";

export const IMAGE_COLUMN_WIDTH = 832;
export const TEXT_COLUMN_WIDTH = 674;
export const IMAGE_COLUMN_PADDING_FALLBACK = 24;
export const TEXT_COLUMN_PADDING_FALLBACK = 48;

export const textWidth = css`
  width: min(${TEXT_COLUMN_WIDTH}px, 100vw - ${TEXT_COLUMN_PADDING_FALLBACK}px);
`;

export const imageWidth = css`
  width: min(
    ${IMAGE_COLUMN_WIDTH}px,
    100vw - ${IMAGE_COLUMN_PADDING_FALLBACK}px
  );
`;

export const GuidePageH1 = styled.h1`
  ${textWidth};
  font-size: 48px;
  font-weight: 900;
  line-height: 57.6px;
  margin-bottom: 16px;

  ${devices.small} {
    font-size: 42px;
    line-height: 50.4px;
  }
`;

export const GuidePageH2 = styled.h2`
  ${textWidth};
  font-size: 22px;
  font-weight: 400;
  line-height: 28.6px;
  margin-bottom: 16px;
`;

export const GuidePageH3 = styled.h3`
  ${textWidth};
  font-size: 32px;
  font-weight: 900;
  line-height: 35.2px;
  margin-bottom: 16px;
  margin-top: 32px;
`;

export const GuidePageH4 = styled(GuidePageH3).attrs({ as: "h4" })`
  font-size: 24px;
  font-weight: 800;
  line-height: 26.4px;
`;

export const GuidePageH5 = styled(GuidePageH3).attrs({ as: "h5" })`
  font-size: 18px;
  font-weight: 800;
  line-height: 19.8px;
`;

export const GuidePageParagraph = styled.p`
  ${textWidth};
  font-size: 18px;
  font-weight: 400;
  line-height: 23.4px;
  margin-bottom: 16px;
`;

export const GuidePageUl = styled.ul`
  ${textWidth};

  list-style: outside;
  list-style-type: disc;

  li {
    margin-left: 1em;
    font-size: 18px;
    font-weight: 400;
    line-height: 23.4px;
    margin-bottom: 16px;
  }
`;

export const GuidePageOl = styled(GuidePageUl).attrs({ as: "ol" })`
  list-style-type: decimal;
`;

export const GuidePageCaption = styled.div`
  ${textWidth};
  font-size: 16px;
  font-weight: 400;
  line-height: 20.8px;
  color: var(--spectrum-gray-600);

  margin-left: auto;
  margin-right: auto;
  margin-top: 16px;
`;
